import React, { useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { moduleSettingsAPI } from '@viilutalous/common';
import {
  useRegisterStoreItems, useStore, useReportStoreItems, useWidgetStoreItems, useModuleStoreItems,
} from '@viilutalous/store';
import { REPORT_MODULES, SYSTEM_TEMPLATES } from '@viilutalous/reporting';
import { WIDGET_REGISTRY, ICON_MAP, LINK_PRESETS, JUMBOTRON_PRESETS } from '@viilutalous/dashboard';
import { useTenant } from '../contexts/TenantContext';

export default function StoreRegistrar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const { enabledModules, toggleModule, enabledReports, setEnabledReports } = useStore();
  const saveTimeoutRef = useRef(null);
  const pendingSaveRef = useRef(null);
  const reportsRef = useRef(enabledReports);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadedTenantId, setLoadedTenantId] = useState(null);

  // The pending write owns its tenant id, even after navigation changes context.
  const flushPendingSave = useCallback(() => {
    clearTimeout(saveTimeoutRef.current);
    const pending = pendingSaveRef.current;
    pendingSaveRef.current = null;
    if (!pending) return;
    moduleSettingsAPI.update(pending.tenantId, 'report_store', { enabled_reports: pending.reports }).catch((err) => {
      console.error('StoreRegistrar: Error saving report store preferences:', err);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadedTenantId(null);
    setLoadFailed(false);
    reportsRef.current = new Set();
    setEnabledReports(new Set());
    if (!tenantId) return;
    (async () => {
      try {
        const result = await moduleSettingsAPI.get(tenantId, 'report_store');
        if (cancelled) return;
        const reports = new Set(result?.data?.settings?.enabled_reports || []);
        reportsRef.current = reports;
        setEnabledReports(reports);
        setLoadedTenantId(tenantId);
      } catch (err) {
        if (!cancelled) setLoadFailed(true);
        console.error('StoreRegistrar: Error loading report store settings:', err);
      }
    })();
    return () => {
      cancelled = true;
      flushPendingSave();
    };
  }, [tenantId, loadAttempt, setEnabledReports, flushPendingSave]);

  const handleToggleReport = useCallback((key) => {
    if (loadedTenantId !== tenantId) return;
    const next = new Set(reportsRef.current);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    reportsRef.current = next;
    setEnabledReports(next);
    pendingSaveRef.current = { tenantId, reports: [...next] };
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(flushPendingSave, 500);
  }, [loadedTenantId, tenantId, setEnabledReports, flushPendingSave]);

  const handleCustomizeReport = useCallback((report) => {
    const systemTemplateName = SYSTEM_TEMPLATES[report.key];
    if (!systemTemplateName) return;
    const reportName = t(report.label);
    navigate(`/app/report-designer?system=${encodeURIComponent(systemTemplateName)}&name=${encodeURIComponent(reportName)}`);
  }, [navigate, t]);

  const reportStoreItems = useReportStoreItems({
    reportModules: REPORT_MODULES,
    systemTemplates: SYSTEM_TEMPLATES,
    enabledReports,
    onToggleReport: handleToggleReport,
    onCustomize: handleCustomizeReport,
  });

  const emptyWidgets = useMemo(() => [], []);
  const emptyConfigs = useMemo(() => ({}), []);
  const noopFn = useCallback(() => {}, []);

  const widgetStoreItems = useWidgetStoreItems({
    widgetCatalog: WIDGET_REGISTRY,
    activeWidgets: emptyWidgets,
    widgetConfigs: emptyConfigs,
    onAddWidget: noopFn,
    onRemoveWidget: noopFn,
    onUpdateWidgetConfig: noopFn,
    iconMap: ICON_MAP,
    linkPresets: LINK_PRESETS,
    jumbotronPresets: JUMBOTRON_PRESETS,
  });

  const handleRequestModuleAccess = useCallback((key) => {
    const moduleName = t(`store.module.${key}`);
    window.alert(t('store.request_access_sent', { module: moduleName }));
  }, [t]);

  const moduleStoreItems = useModuleStoreItems({
    enabledModules,
    onToggleModule: toggleModule,
    onRequestAccess: handleRequestModuleAccess,
    tenantId,
  });

  useRegisterStoreItems('report', reportStoreItems);
  useRegisterStoreItems('widget_catalog', widgetStoreItems);
  useRegisterStoreItems('module', moduleStoreItems);

  return (
    <Snackbar open={loadFailed}>
      <Alert severity="warning" action={<Button color="inherit" onClick={() => setLoadAttempt((value) => value + 1)}>{t('common.report_store_retry')}</Button>}>
        {t('common.report_store_load_failed')}
      </Alert>
    </Snackbar>
  );
}
